import { Icon } from "@mdi/react";
import { useState } from "react";
import { useDispatch } from "react-redux";

import { mdiAlert, mdiAlertCircle, mdiChevronRight, mdiInformation } from "@mdi/js";
import { formatDistanceToNow } from "date-fns";
import { setSystemStatus, setSystemStatusPolling } from "store/features/app/appSlice";
import styled from "styled-components";

import { useSystemStatusSelector } from "hooks/useSystemStatusSelector";

import { getSystemStatus } from "api/systemStatus";

const SystemStatusMenuItem = () => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const { currentStatus, lastChecked, isPolling } = useSystemStatusSelector();

  const checkNow = async () => {
    try {
      dispatch(setSystemStatusPolling(true));
      const status = await getSystemStatus();
      dispatch(setSystemStatus(status));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to check system status:", error);
    } finally {
      dispatch(setSystemStatusPolling(false));
    }
  };

  const getStatusText = () => {
    if (isPolling) {
      return "Checking system status...";
    }

    if (!currentStatus) {
      return "All systems operational";
    }

    return currentStatus.message;
  };

  const getStatusIcon = () => {
    if (!currentStatus) return null;

    switch (currentStatus.type) {
      case "major":
        return mdiAlert;
      case "partial":
        return mdiAlertCircle;
      case "planned":
        return mdiInformation;
      default:
        return null;
    }
  };

  const getStatusIconColor = () => {
    if (!currentStatus) return "var(--color-text-40)";

    switch (currentStatus.type) {
      case "major":
        return "var(--color-danger)";
      case "partial":
        return "var(--orange)";
      case "planned":
        return "var(--color-primary)";
      default:
        return "var(--color-text-40)";
    }
  };

  const handleToggleExpanded = () => {
    setExpanded(!expanded);
  };

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    checkNow();
  };

  return (
    <StatusContainer>
      <StatusMainItem onClick={handleToggleExpanded} expanded={expanded}>
        <StatusContent>
          <StatusTextWrapper>
            <StatusLabel>
              System Status
              {currentStatus && getStatusIcon() && (
                <StatusIconIndicator color={getStatusIconColor()}>
                  <Icon path={getStatusIcon()} size="14px" />
                </StatusIconIndicator>
              )}
            </StatusLabel>
          </StatusTextWrapper>
          <ChevronIcon expanded={expanded}>
            <Icon path={mdiChevronRight} size="16px" />
          </ChevronIcon>
        </StatusContent>
      </StatusMainItem>

      {expanded && (
        <StatusDetails>
          <StatusDetailRow>
            <StatusMessage type={currentStatus?.type || "success"}>
              {getStatusText()}
            </StatusMessage>
          </StatusDetailRow>

          <StatusDetailRow>
            <StatusDetailLabel>Status Type:</StatusDetailLabel>
            <StatusDetailValue type={currentStatus?.type || "success"}>
              {currentStatus?.type || "operational"}
            </StatusDetailValue>
          </StatusDetailRow>

          {lastChecked && (
            <StatusDetailRow>
              <StatusDetailLabel>Last Checked:</StatusDetailLabel>
              <StatusDetailValue>
                {formatDistanceToNow(lastChecked, { addSuffix: true })}
              </StatusDetailValue>
            </StatusDetailRow>
          )}

          <RefreshButton onClick={handleRefresh} disabled={isPolling}>
            <Icon path={mdiInformation} size="14px" spin={isPolling} />
            {isPolling ? "Checking..." : "Check Now"}
          </RefreshButton>
        </StatusDetails>
      )}
    </StatusContainer>
  );
};

export default SystemStatusMenuItem;

const StatusContainer = styled.div`
  background-color: var(--white);
`;

const StatusMainItem = styled.div<{ expanded: boolean }>`
  display: flex;
  align-items: center;
  min-height: 2.8rem;
  padding: 0 var(--space-5);
  cursor: pointer;
  transition: background-color var(--duration-short) var(--ease);
  background-color: ${({ expanded }) =>
    expanded ? "var(--color-text-06)" : "var(--white)"};

  &:hover {
    background-color: var(--color-text-06);
  }
`;

const StatusContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const StatusTextWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

const StatusLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 2.8rem;
  overflow: hidden;
  cursor: pointer;

  &.no-pointer {
    pointer-events: none;
  }
`;

const StatusIconIndicator = styled.div<{ color: string }>`
  display: flex;
  align-items: center;
  color: ${({ color }) => color};
  flex-shrink: 0;
`;

const StatusMessage = styled.div<{ type: string }>`
  font-size: 12px;
  color: ${({ type }) => {
    switch (type) {
      case "major":
        return "var(--color-danger)";
      case "partial":
        return "var(--orange)";
      case "planned":
        return "var(--color-primary)";
      case "success":
        return "var(--color-success)";
      default:
        return "var(--color-text-60)";
    }
  }};
  line-height: 1.3;
`;

const ChevronIcon = styled.div<{ expanded: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  transition: transform 0.2s;
  transform: rotate(${({ expanded }) => (expanded ? "90deg" : "0deg")});
  color: var(--color-text-40);
`;

const StatusDetails = styled.div`
  padding: 12px var(--space-5);
  background-color: var(--color-text-02);
  border-top: 1px solid var(--color-text-10);
`;

const StatusDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 6px;

  &:last-of-type {
    margin-bottom: 8px;
  }
`;

const StatusDetailLabel = styled.span`
  font-size: 12px;
  color: var(--color-text-60);
`;

const StatusDetailValue = styled.span<{ type?: string }>`
  font-size: 12px;
  font-weight: var(--fontWeightMedium);
  text-transform: capitalize;
  color: ${({ type }) => {
    if (!type) return "var(--color-text)";
    switch (type) {
      case "major":
        return "var(--color-danger)";
      case "partial":
        return "var(--orange)";
      case "planned":
        return "var(--color-primary)";
      case "success":
        return "var(--color-success)";
      default:
        return "var(--color-text)";
    }
  }};
`;

const RefreshButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 12px;
  background: transparent;
  border: 1px solid var(--color-text-20);
  border-radius: 4px;
  font-size: 12px;
  color: var(--color-text-60);
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: var(--color-text-05);
    border-color: var(--color-text-30);
    color: var(--color-text);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
